import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DotDotDotIcon, PrivateIcon, PublicIcon, Spinner, } from '@streamr/tv-ui';
import { useRef, useState } from 'react';
import { keyframes, styled } from 'styled-components';
import { formatHowLongAgo } from '../utils/dateFormatters';
import { Anchor } from './Anchor';
import { showRecordingOptionsPopup } from './popups/recording/RecordingOptionsPopup';
export const RecordingsList = ({ recordings, status, variant, hasMore, onLoadMore, }) => {
    return (_jsxs(Root, { children: [(status === 'loaded' || status === 'loadingMore') &&
                recordings.length > 0 && (_jsx(RecordingsListGrid, { children: recordings.map((recording) => variant === 'viewer' ? (_jsx(RecordingListItemForViewer, { recording: recording }, recording.url)) : (_jsx(RecoringListItemForBroadcaster, { recording: recording }, recording.url))) })), status === 'loaded' && recordings.length === 0 && (_jsx(NoRecordings, { children: "No recordings found for this broadcast" })), status === 'error' && (_jsx(NoRecordings, { children: "An error occured while fetching the recordings. Please try again later." })), (status === 'loading' || status === 'loadingMore') && (_jsx(SpinnerContainer, { children: _jsx(StyledSpinner, {}) })), hasMore && status !== 'error' && (_jsx(LoadMoreContainer, { children: _jsx(LoadMoreButton, { type: "button", onClick: onLoadMore, disabled: status === 'loadingMore', children: "Load more" }) }))] }));
};
const RecordingListItemForViewer = ({ recording, }) => {
    const [loadingState, setLoadingState] = useState('loading');
    return (_jsxs(Item, { tag: "a", href: recording.url, target: "_blank", children: [_jsxs(ItemMedia, { children: [loadingState === 'loading' && _jsx(ItemSkeleton, {}), loadingState === 'errored' && (_jsx(NoImage, { children: "Image not available" })), (loadingState === 'loading' || loadingState === 'loaded') && (_jsx(ItemImage, { src: recording.thumbnailUrl, alt: recording.url, loading: "lazy", onLoad: () => {
                            setLoadingState('loaded');
                        }, onError: () => {
                            setLoadingState('errored');
                        }, "data-loaded": loadingState === 'loaded' }))] }), _jsx(ItemDate, { children: formatHowLongAgo(new Date(recording.timestamp)) })] }));
};
const RecoringListItemForBroadcaster = ({ recording, }) => {
    const [loadingState, setLoadingState] = useState('loading');
    return (_jsxs(Item, { tag: "a", href: recording.url, target: "_blank", children: [_jsxs(ItemMedia, { children: [loadingState === 'loading' && _jsx(ItemSkeleton, {}), loadingState === 'errored' && (_jsx(NoImage, { children: "Image not available" })), (loadingState === 'loading' || loadingState === 'loaded') && (_jsx(ItemImage, { src: recording.thumbnailUrl, loading: "lazy", onLoad: () => {
                            setLoadingState('loaded');
                        }, onError: () => {
                            setLoadingState('errored');
                        }, "data-loaded": loadingState === 'loaded' }))] }), _jsxs(ItemAccess, { children: [recording.isPublic ? _jsx(PublicIcon, {}) : _jsx(PrivateIcon, {}), _jsx(ItemAccessText, { children: recording.isPublic ? 'Public' : 'Private' })] }), _jsx(RecordingOptionsPopupTrigger, { recording: recording })] }));
};
const RecordingOptionsPopupTrigger = ({ recording, }) => {
    const anchorRef = useRef(null);
    return (_jsx(PositionedAnchor, { ref: anchorRef, children: _jsx(ItemButton, { type: "button", onClick: (event) => {
                event.preventDefault();
                showRecordingOptionsPopup({
                    anchorRef,
                    variant: 'desktop',
                    recording,
                });
            }, children: _jsx(DotDotDotIcon, {}) }) }));
};
const Root = styled.div `
    width: 100%;
    display: inline-flex;
    flex-direction: column;
    gap: 1rem;
`;
const NoRecordings = styled.div `
    margin: 1rem 0;
`;
const RecordingsListGrid = styled.div `
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(1, 1fr);
    @media (min-width: 40rem) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 60rem) {
        grid-template-columns: repeat(3, 1fr);
    }
`;
const Item = styled(Button) `
    position: relative;
    overflow: hidden;
    border-radius: 1.5rem;
    --Button_HoverTransform: scale(1.01);
    --Button_ActiveTransform: scale(1.01);
    color: var(--tv-ui-color-3);
    width: 100%;
`;
const ItemImage = styled.img.attrs({ draggable: false }) `
    width: 100%;
    height: 100%;
    object-fit: cover;
    -webkit-user-drag: none;
    user-select: none;
    -webkit-user-select: none;
    opacity: 0;
    transition: opacity 0.25s ease;

    &[data-loaded='true'] {
        opacity: 1;
    }
`;
const ItemMedia = styled.div `
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    `;
const NoImage = styled.div `
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 1.5rem;
    background-color: var(--tv-ui-color-15);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    box-sizing: border-box;
    font-weight: 400;
    font-size: 0.875rem;
    color: var(--tv-ui-color-16);
`;
const shimmer = keyframes `
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;
const ItemSkeleton = styled.div `
    position: absolute;
    inset: 0;
    background: linear-gradient(
        90deg,
        var(--tv-ui-color-15) 25%,
        var(--tv-ui-color-14) 37%,
        var(--tv-ui-color-15) 63%
    );
    background-size: 400% 100%;
    animation: ${shimmer} 2.5s linear infinite;
`;
const ItemDate = styled.span `
    position: absolute;
    top: 0;
    left: 0;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    border-radius: 1rem;
    padding: 0.25rem 0.5rem;
    margin: 1rem;
`;
const ItemAccessText = styled.div `
    margin-left: 0;
    white-space: nowrap;
    overflow: hidden;
    max-width: 0;
    opacity: 0;
    transition: max-width 0.2s ease, margin-left 0.2s ease, opacity 0.2s ease;
    position: relative;
`;
const ItemAccess = styled.div `
    position: absolute;
    top: 0;
    left: 0;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    margin: 1rem;
    padding: 0.5rem;
    border-radius: 1rem;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 2rem;
    max-height: 2rem;
    box-sizing: border-box;

    &:hover {
        ${ItemAccessText} {
            max-width: 20ch;
            margin-left: 0.5rem;
            opacity: 1;
        }
    }
`;
const ItemButton = styled(Button) `
    padding: 0.5rem;
    border-radius: 1rem;
    backdrop-filter: blur(3rem);
    --Button_HoverTransform: scale(1.05);
    --Button_ActiveTransform: scale(1.05);
    --Button_Background: var(--tv-ui-color-15);
    --Button_ActiveBackground: var(--tv-ui-color-14);
    color: var(--tv-ui-color-3);
`;
const PositionedAnchor = styled(Anchor) `
    position: absolute;
    top: 0;
    right: 0;
    margin: 1rem;
    display: inline-flex;
`;
const SpinnerContainer = styled.div `
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 0;
`;
const StyledSpinner = styled(Spinner) `
    --Spinner_Size: 3rem;
    --Spinner_TrackColor: var(--tv-ui-color-2);
    --Spinner_Color: var(--tv-ui-color-3);
    --Spinner_Thickness: 3px;
`;
const LoadMoreContainer = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
`;
const LoadMoreButton = styled(Button) `
    --Button_HoverTransform: scale(1.05);
    --Button_ActiveTransform: scale(1.05);
    --Button_Background: var(--tv-ui-color-15);
    --Button_ActiveBackground: var(--tv-ui-color-15);
    --Button_Padding: 1rem 2rem;
    --Button_BorderRadius: 1rem;
    color: var(--tv-ui-color-3);
`;
